import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Caption1 } from '@ui/Typography/Typography'

import { Button } from '../Button'
import type { StepKind } from './Tourtip'

import styles from './Tourtip.module.scss'

interface Props {
  kind: StepKind
  index: number
  total: number
  onPrev: () => void
  onNext: () => void

  introActions?: IntroActions
  outroActions?: OutroActions
}

interface ActionConfig {
  onClick: () => void
  text?: string
}

interface IntroActions {
  secondary: ActionConfig
  primary: ActionConfig
}

interface OutroActions {
  secondary: ActionConfig
  primary: ActionConfig
}

const defaultIntroActions: IntroActions = {
  secondary: { onClick: () => {}, text: 'tour.Later' },
  primary: { onClick: () => {}, text: 'tour.Take a tour' },
}

const defaultOutroActions: OutroActions = {
  secondary: { onClick: () => {}, text: 'tour.Got It' },
  primary: { onClick: () => {}, text: 'tour.Done' },
}

export const TourActions: FC<Props> = ({
  kind,
  total,
  index,
  onPrev,
  onNext,
  introActions,
  outroActions,
}) => {
  const { t } = useTranslation()

  const mergedIntroActions = useMemo(
    () => ({
      secondary: {
        ...defaultIntroActions.secondary,
        ...introActions?.secondary,
        text: introActions?.secondary?.text ?? defaultIntroActions.secondary.text,
      },
      primary: {
        ...defaultIntroActions.primary,
        ...introActions?.primary,
        text: introActions?.primary?.text ?? defaultIntroActions.primary.text,
      },
    }),
    [introActions]
  )

  const mergedOutroActions = useMemo(
    () => ({
      secondary: {
        ...defaultOutroActions.secondary,
        ...outroActions?.secondary,
        text: outroActions?.secondary?.text ?? defaultOutroActions.secondary.text,
      },
      primary: {
        ...defaultOutroActions.primary,
        ...outroActions?.primary,
        text: outroActions?.primary?.text ?? defaultOutroActions.primary.text,
      },
    }),
    [outroActions]
  )

  const renderButtons = (actions: { secondary: ActionConfig; primary: ActionConfig }) => (
    <>
      <Button variant='secondary' size='sm' onClick={actions.secondary.onClick}>
        {actions.secondary.text ? t(actions.secondary.text) : ''}
      </Button>
      <Button variant='primary' size='sm' onClick={actions.primary.onClick}>
        {actions.primary.text ? t(actions.primary.text) : ''}
      </Button>
    </>
  )

  return (
    <div className={styles.actions}>
      {total > 1 && (
        <div className={styles.counter}>
          <Caption1>
            {index + 1} {t('tour.of')} {total}
          </Caption1>
        </div>
      )}

      {kind === 'regular' && (
        <>
          <Button variant='secondary' size='sm' onClick={onPrev}>
            {t('tour.Previous')}
          </Button>
          <Button variant='secondary' size='sm' onClick={onNext}>
            {t('tour.Next')}
          </Button>
        </>
      )}

      {kind === 'intro' && renderButtons(mergedIntroActions)}
      {kind === 'outro' && renderButtons(mergedOutroActions)}
    </div>
  )
}
